import React, { useState, useEffect, useContext } from 'react';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';
import { AuthContext } from '../contexts/AuthContext';
import { buildApiUrl } from '../config/environment';

const LinkTransactionModal = ({
  show,
  onClose,
  payment,
  paymentDate,
  property,
  agencyCurrency,
  onTransactionLinked
}) => {
  const { user, agencyCountry } = useContext(AuthContext);
  const currencySymbol = getCurrencySymbol(agencyCurrency);
  
  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    if (!show || !paymentDate) return;

    const bankAccountId = property?.agency_bank_account?.id;
    if (!bankAccountId) return;

    fetchTransactionsByDate(paymentDate, bankAccountId);
  }, [show, paymentDate, property]);

  const fetchTransactionsByDate = async (date, bankAccountId) => {
    setLoadingTransactions(true);
    setSelectedTransaction(null);
    try {
      const searchDate = date.split('T')[0];
      const queryParams = new URLSearchParams({
        date_from: searchDate,
        date_to: searchDate,
        limit: '50',
        sort_by: 'transaction_date',
        sort_dir: 'desc',
        transaction_type: 'credit'
      });

      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
      const fullUrl = `${baseUrl}/bank-accounts/${bankAccountId}/transactions/search?${queryParams.toString()}`;

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch transactions');
      }

      const data = await response.json();
      setTransactions(data.data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleTransactionSelect = (transaction) => {
    setSelectedTransaction(transaction);
  };

  const handleLinkTransaction = async () => {
    if (!selectedTransaction || !payment?.id) return;

    const confirmMsg = "Link this transaction to the payment?";
    if (!window.confirm(confirmMsg)) return;

    try {
      const response = await fetch(buildApiUrl(`/payments/${payment.id}/link-transaction`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          transaction_id: selectedTransaction.ID
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to link transaction');
      }

      alert('Transaction linked successfully!');

      // Notify parent to refresh payment data
      if (onTransactionLinked) {
        onTransactionLinked();
      }

      onClose();
    } catch (error) {
      console.error('Error linking transaction:', error);
      alert(`Failed to link transaction: ${error.message}`);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[10000]" style={{ backdropFilter: 'blur(2px)' }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden relative flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex-shrink-0">
          <h5 className="text-lg font-semibold flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            Link Transaction
          </h5>
          <button
            type="button"
            className="absolute top-3 right-3 text-blue-100 hover:text-white transition-colors duration-200"
            onClick={onClose}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-1">Payment Date: <span className="font-medium text-gray-800">{paymentDate ? formatDateWithContext(paymentDate) : 'N/A'}</span></p>
            <p className="text-xs text-gray-500">Showing credit transactions for this date</p>
          </div>

          {loadingTransactions ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Loading transactions...</p>
            </div>
          ) : transactions.length > 0 ? (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Available Transactions ({transactions.length})</p>
              <div className="border border-gray-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                {transactions.map((transaction) => (
                  <label
                    key={transaction.ID}
                    className={`flex items-center p-3 cursor-pointer transition-colors border-b border-gray-200 last:border-b-0 ${
                      selectedTransaction?.ID === transaction.ID 
                        ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="selectedTransaction"
                      checked={selectedTransaction?.ID === transaction.ID}
                      onChange={() => handleTransactionSelect(transaction)}
                      className="w-4 h-4 text-blue-600 flex-shrink-0"
                    />
                    <div className="ml-3 flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-medium text-gray-800 truncate">{transaction.Description}</span>
                        <span className="text-sm font-semibold text-emerald-600 flex-shrink-0">
                          {transaction.Amount?.formatted || `${currencySymbol}${(transaction.Amount?.amount / 100).toFixed(2)}`}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Transaction ID: {transaction.ID} • {formatDateWithContext(transaction.TransactionDate)}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm text-gray-500">No matching transactions found for this date</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            onClick={handleLinkTransaction}
            disabled={!selectedTransaction}
          >
            Link Transaction
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkTransactionModal;
